import { ReactComponent as ChevronLeftIcon } from '@assets/images/app/chevron-left-nutral90.svg';
import { ReactComponent as ChevronRightIcon } from '@assets/images/app/chevron-right-nutral90.svg';
import { ReactComponent as ChevronUpIcon } from '@assets/images/app/chevron-up-nutral90.svg';
import { PreferredScheduleConfig } from '@models/product';
import {
  formatPreferredScheduleAnswer,
  formatPreferredScheduleChipLabel,
  generatePreferredScheduleTimeSlots,
  groupPreferredScheduleSlotsByAmPm,
  parsePreferredScheduleAnswer,
} from '../../../utils/preferred-schedule';
import { getTodayDateKey } from '../../../utils/product-option-date';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';

const PRIMARY = 'var(--Primary-95, #FF3D8F)';

const TimeChip: React.FC<{
  label: string;
  selected?: boolean;
  onClick?: () => void;
}> = ({ label, selected, onClick }) => (
  <button
    type="button"
    className="link-button"
    onClick={onClick}
    style={{
      flex: '1 0 calc(25% - 6px)',
      minWidth: '64px',
      maxWidth: 'calc(25% - 6px)',
      height: '36px',
      padding: '8px 16px',
      borderRadius: '8px',
      border: selected ? `1px solid ${PRIMARY}` : '1px solid #E8EAED',
      background: selected ? '#FFF5F9' : '#FFFFFF',
      color: selected ? '#F82B81' : '#424242',
      fontSize: '14px',
      fontWeight: selected ? 500 : 400,
      lineHeight: '20px',
      boxSizing: 'border-box',
    }}>
    {label}
  </button>
);

interface PreferredScheduleQuestionSectionProps {
  config?: PreferredScheduleConfig;
  value?: string;
  onChange: (value: string) => void;
  /** 증가할 때마다 아코디언을 펼침 (바로구매 시 스크롤 포커스용) */
  focusToken?: number;
}

export const PreferredScheduleQuestionSection: React.FC<PreferredScheduleQuestionSectionProps> = (props) => {
  const { config, value, onChange, focusToken } = props;
  const parsed = parsePreferredScheduleAnswer(value);
  const [open, setOpen] = useState(true);
  const [selectedDateKey, setSelectedDateKey] = useState<string | undefined>(parsed.date);
  const [selectedTime, setSelectedTime] = useState<string | undefined>(parsed.time);
  const todayKey = getTodayDateKey();

  useEffect(() => {
    const next = parsePreferredScheduleAnswer(value);
    setSelectedDateKey(next.date);
    setSelectedTime(next.time);
  }, [value]);

  useEffect(() => {
    if (focusToken == null || focusToken <= 0) return;
    setOpen(true);
  }, [focusToken]);

  const timeSlots = useMemo(() => generatePreferredScheduleTimeSlots(config), [config]);
  const { am, pm } = useMemo(() => groupPreferredScheduleSlotsByAmPm(timeSlots), [timeSlots]);

  const handleSelectDate = (date: Date) => {
    const nextDateKey = moment(date).format('YYYY-MM-DD');
    if (nextDateKey < todayKey) {
      return;
    }
    setSelectedDateKey(nextDateKey);
    if (selectedTime) {
      onChange(formatPreferredScheduleAnswer(nextDateKey, selectedTime));
    } else {
      onChange('');
    }
  };

  const handleSelectTime = (time: string) => {
    if (!selectedDateKey) {
      return;
    }
    setSelectedTime(time);
    onChange(formatPreferredScheduleAnswer(selectedDateKey, time));
  };

  return (
    <div style={{ marginBottom: '8px' }}>
      <button
        type="button"
        className="link-button"
        onClick={() => setOpen((prev) => !prev)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: open ? '15px' : 0,
          color: '#242424',
          fontSize: '18px',
          fontWeight: 700,
          lineHeight: '24px',
          textAlign: 'left',
        }}>
        <span>희망 날짜와 시간을 선택해 주세요</span>
        <ChevronUpIcon
          width={20}
          height={20}
          style={{
            flexShrink: 0,
            transform: open ? 'none' : 'rotate(180deg)',
            transition: 'transform 0.2s',
          }}
        />
      </button>

      {open && (
        <>
          <div className="preferred-schedule-calendar" style={{ marginBottom: '16px' }}>
            <Calendar
              className="preferred-schedule-calendar__widget"
              calendarType="gregory"
              locale="ko-KR"
              view="month"
              maxDetail="month"
              minDetail="month"
              value={selectedDateKey ? moment(selectedDateKey).toDate() : undefined}
              minDate={moment(todayKey).toDate()}
              maxDate={moment(todayKey).add(1, 'year').toDate()}
              onClickDay={handleSelectDate}
              tileDisabled={({ date, view }) => {
                if (view !== 'month') return false;
                return moment(date).format('YYYY-MM-DD') < todayKey;
              }}
              tileClassName={({ date, view }) => {
                if (view !== 'month') return null;
                const key = moment(date).format('YYYY-MM-DD');
                if (selectedDateKey === key) return 'preferred-schedule-selected';
                return null;
              }}
              prev2Label={null}
              next2Label={null}
              prevLabel={<ChevronLeftIcon />}
              nextLabel={<ChevronRightIcon />}
              showNeighboringMonth={false}
              formatDay={(_locale, date) => date.getDate().toString()}
              navigationLabel={({ date }) => moment(date).format('YYYY. MM')}
            />
            <style>{`
              .preferred-schedule-calendar .react-calendar {
                width: 100%;
                border: none;
                font-family: Pretendard, sans-serif;
              }
              .preferred-schedule-calendar .react-calendar__navigation {
                height: 44px;
                margin-bottom: 4px;
              }
              .preferred-schedule-calendar .react-calendar__navigation__arrow:disabled {
                opacity: 0.3 !important;
                cursor: not-allowed !important;
                background-color: #ffffff !important;
              }
              .preferred-schedule-calendar .react-calendar__navigation__arrow:disabled svg path {
                stroke: #D5D8DD !important;
              }
              .preferred-schedule-calendar .react-calendar__navigation__label {
                font-size: 18px;
                font-weight: 700;
                color: #242424;
              }
              .preferred-schedule-calendar .react-calendar__navigation__label:disabled {
                cursor: default !important;
                pointer-events: none !important;
                background-color: #ffffff !important;
              }
              .preferred-schedule-calendar .react-calendar__month-view__weekdays {
                font-size: 14px;
                color: #424242;
              }
              .preferred-schedule-calendar .react-calendar__month-view__weekdays__weekday {
                padding: 0;
                height: 32px;
                display: flex;
                align-items: center;
                justify-content: center;
              }
              .preferred-schedule-calendar .react-calendar__month-view__weekdays__weekday abbr {
                text-decoration: none;
              }
              /* Figma Day: px 10 / py 4, date top-aligned
                 선택 시 border 추가로 숫자가 밀리지 않도록 기본에도 투명 border 유지 */
              .preferred-schedule-calendar .react-calendar__tile {
                display: flex;
                flex-direction: column;
                justify-content: flex-start;
                align-items: center;
                padding: 4px 10px;
                background: transparent;
                color: #424242;
                font-size: 15px;
                font-weight: 700;
                line-height: 1;
                border: 1px solid transparent;
                border-radius: 8px !important;
                box-sizing: border-box;
              }
              .preferred-schedule-calendar .react-calendar__tile abbr {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 24px;
                height: 24px;
                text-decoration: none;
                line-height: 1;
                font-weight: inherit;
              }
              .preferred-schedule-calendar .react-calendar__tile:disabled {
                color: #D5D8DD;
                background: transparent;
                border-color: transparent;
              }
              .preferred-schedule-calendar .react-calendar__tile:enabled:hover,
              .preferred-schedule-calendar .react-calendar__tile:enabled:focus {
                border-radius: 8px !important;
              }
              .preferred-schedule-calendar .react-calendar__tile--now {
                background: transparent !important;
              }
              .preferred-schedule-calendar .react-calendar__tile--now:enabled:hover {
                border-radius: 8px !important;
              }
              /* Figma 8967:36934 — 선택: solid fill 없음, 연한 배경 + 핑크 보더/텍스트 */
              .preferred-schedule-calendar .react-calendar__tile--active,
              .preferred-schedule-calendar .react-calendar__tile--active:enabled:hover,
              .preferred-schedule-calendar .react-calendar__tile--active:enabled:focus,
              .preferred-schedule-calendar .react-calendar__tile.preferred-schedule-selected,
              .preferred-schedule-calendar .react-calendar__tile.preferred-schedule-selected:enabled:hover,
              .preferred-schedule-calendar .react-calendar__tile.preferred-schedule-selected:enabled:focus {
                background: #FFF5F9 !important;
                color: #FF3D8F !important;
                border-color: #FF3D8F;
                border-radius: 8px !important;
              }
              .preferred-schedule-calendar .react-calendar__tile--active abbr,
              .preferred-schedule-calendar .react-calendar__tile.preferred-schedule-selected abbr {
                color: #FF3D8F !important;
              }
            `}</style>
          </div>

          {selectedDateKey && timeSlots.length > 0 && (
            <>
              <div style={{ height: 1, backgroundColor: '#EFF1F4', margin: '20px 0 16px' }} />
              {am.length > 0 && (
                <div style={{ marginBottom: '8px' }}>
                  <div
                    style={{
                      marginBottom: '8px',
                      color: '#424242',
                      fontSize: '14px',
                      fontWeight: 700,
                      lineHeight: 'normal',
                    }}>
                    오전
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {am.map((slot: string) => (
                      <TimeChip
                        key={slot}
                        label={formatPreferredScheduleChipLabel(slot)}
                        selected={selectedTime === slot}
                        onClick={() => handleSelectTime(slot)}
                      />
                    ))}
                  </div>
                </div>
              )}
              {pm.length > 0 && (
                <div>
                  <div
                    style={{
                      marginBottom: '8px',
                      color: '#424242',
                      fontSize: '14px',
                      fontWeight: 700,
                      lineHeight: 'normal',
                    }}>
                    오후
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {pm.map((slot: string) => (
                      <TimeChip
                        key={slot}
                        label={formatPreferredScheduleChipLabel(slot)}
                        selected={selectedTime === slot}
                        onClick={() => handleSelectTime(slot)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};
